import { Button, Group, SimpleGrid, Stack, Text, TextInput } from '@mantine/core'
import { useForm } from '@mantine/form'
import { isValidFax, type SelectedRecipient } from './referralTypes'

type Props = {
  specialtyLabel: string
  onSave: (recipient: SelectedRecipient) => void
  onCancel: () => void
}

// For a provider no search finds. Used for this referral and remembered in the clinic's
// Frequently used list once sent — no approval step, it is the clinic's own contact.
export const ManualRecipientForm = ({ specialtyLabel, onSave, onCancel }: Props) => {
  const form = useForm({
    initialValues: {
      doctorName: '',
      credential: '',
      practiceName: '',
      street: '',
      city: '',
      state: '',
      zip: '',
      phone: '',
      fax: '',
      npi: '',
    },
    validate: {
      doctorName: (v, values) =>
        v.trim() || values.practiceName.trim() ? null : 'Enter a doctor or practice name',
      street: (v) => (v.trim() ? null : 'Required'),
      city: (v) => (v.trim() ? null : 'Required'),
      state: (v) => (/^[A-Za-z]{2}$/.test(v.trim()) ? null : 'Two-letter state'),
      zip: (v) => (/^\d{5}(-?\d{4})?$/.test(v.trim()) ? null : 'Five-digit ZIP'),
      fax: (v) => (isValidFax(v) ? null : 'Enter a 10-digit US fax number'),
      npi: (v) => (!v.trim() || /^\d{10}$/.test(v.trim()) ? null : 'An NPI has 10 digits'),
    },
  })

  const submit = form.onSubmit((v) => {
    const words = v.doctorName
      .trim()
      .replace(/^dr\.?\s+/i, '')
      .split(/\s+/)
      .filter(Boolean)
    const lastName = words.length ? words[words.length - 1] : undefined
    const firstName = words.length > 1 ? words[0] : undefined
    onSave({
      firstName,
      lastName,
      credential: v.credential.trim() || undefined,
      practiceName: v.practiceName.trim() || undefined,
      npi: v.npi.trim() || undefined,
      specialtyLabel,
      street: v.street.trim(),
      city: v.city.trim(),
      state: v.state.trim().toUpperCase(),
      zip: v.zip.trim(),
      phone: v.phone.trim() || undefined,
      fax: v.fax.trim(),
      faxCandidates: [],
      manual: true,
    })
  })

  return (
    <form onSubmit={submit}>
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          The referral letter is addressed to the doctor when you name one, otherwise to
          &ldquo;Colleague&rdquo;.
        </Text>
        <SimpleGrid cols={2}>
          <TextInput
            label="Doctor name"
            placeholder="e.g. Maria Alvarez"
            {...form.getInputProps('doctorName')}
          />
          <TextInput
            label="Credential"
            placeholder="MD, DO, NP…"
            {...form.getInputProps('credential')}
          />
        </SimpleGrid>
        <TextInput label="Practice or clinic name" {...form.getInputProps('practiceName')} />
        <TextInput label="Street address" withAsterisk {...form.getInputProps('street')} />
        <SimpleGrid cols={3}>
          <TextInput label="City" withAsterisk {...form.getInputProps('city')} />
          <TextInput label="State" maxLength={2} withAsterisk {...form.getInputProps('state')} />
          <TextInput label="ZIP" withAsterisk {...form.getInputProps('zip')} />
        </SimpleGrid>
        <SimpleGrid cols={2}>
          <TextInput label="Fax number" withAsterisk {...form.getInputProps('fax')} />
          <TextInput label="Phone" {...form.getInputProps('phone')} />
        </SimpleGrid>
        <TextInput label="NPI (optional)" {...form.getInputProps('npi')} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" color="teal">
            Use this provider
          </Button>
        </Group>
      </Stack>
    </form>
  )
}
